import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { type NativeWorkspaceAction, nativeDesktopPlatform } from '@/lib/native-desktop';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';

export type WorkspaceActions = {
  runWorkspaceAction: (action: NativeWorkspaceAction) => void;
};

/**
 * Runs a configured workspace action by dropping its command into the terminal
 * side panel. The platform-specific `scripts[platform]` variant wins when it is
 * present and non-blank; otherwise we fall back to the generic `script`. State
 * lives in the store (read lazily via `getState()` so each call sees the latest
 * `workspaceEnvironment`), and a blank command is treated as a no-op.
 */
export function createWorkspaceActions({
  store,
  terminalView,
}: {
  store: DesktopShellStore;
  terminalView?: SidePanelView;
}): WorkspaceActions {
  function runWorkspaceAction(action: NativeWorkspaceAction): void {
    const { workspaceEnvironment, setTerminalAction, setSidePanelOpen } = store.getState();
    const platform = nativeDesktopPlatform();
    const command =
      (platform === null ? null : action.scripts?.[platform])?.trim() || action.script;
    if (command.trim().length === 0) return;
    if (terminalView === undefined) return;
    setTerminalAction({
      id: crypto.randomUUID(),
      name: action.name,
      command,
      env: workspaceEnvironment?.env ?? {},
    });
    setSidePanelOpen(true);
  }

  return { runWorkspaceAction };
}
